/**
 * Generic localStorage→backend sync for the 14 "primary blob" keys
 * migrated in PR2. Replaces the data-loss prone behaviour where these
 * keys would be wiped on every logout/login cycle.
 *
 * Architecture:
 * ─────────────
 *  1. `installUserSettingsAutoSync()` (called once at AuthProvider mount)
 *     wraps `localStorage.setItem`. Whenever a SYNC_KEYS key is written,
 *     a per-key debounced PUT to /user-settings/{key} mirrors the new
 *     value to the backend. Native localStorage behaviour is preserved
 *     for everything else.
 *
 *  2. `hydrateUserSettings()` (called from AuthContext.checkAuth + every
 *     login flow) does ONE GET /user-settings round-trip, then for each
 *     SYNC_KEYS key:
 *      • Backend has a value → write it to localStorage (cross-device pull
 *        wins; this is the source of truth post-migration).
 *      • Backend has no value but localStorage does → push localStorage
 *        upstream (silent first-time migration), set migration_status_*
 *        to "success".
 *      • Failure → migration_status_* = "error" with reason; localStorage
 *        is preserved untouched; next mount retries.
 *
 * Why an `localStorage.setItem` override instead of touching every writer:
 * ──────────────────────────────────────────────────────────────────────
 *  PR2 covers 14 keys written by 13+ files. Modifying each writer would
 *  be invasive and error-prone. The override lets us keep all writers
 *  unchanged — they call `localStorage.setItem(KEY, JSON.stringify(...))`
 *  exactly as before. The wrapper inspects the key, schedules the sync,
 *  and forwards to the native implementation.
 *
 *  Risks: (a) third-party libs that override localStorage themselves
 *  could clobber our wrapper; we install last in AuthProvider.useEffect
 *  and idempotently. (b) Unit tests that mock localStorage may need to
 *  also clear the wrapper — we expose `__wrappedKeys` for diagnostics.
 */

import { userSettingsApi } from './api';

const SYNC_KEYS = [
  // Personal budget
  'patrimony_personal_cats_v1',
  'patrimony_budget_allocations_v2',
  'patrimony_pending_sales_v1',
  // Family budget
  'patrimony_family_cats_v1',
  'patrimony_family_recurring_v1',
  'patrimony_family_goals_v1',
  'patrimony_family_alloc_v1',
  'patrimony_family_used_alloc_v1',
  'patrimony_family_current_alloc_v1',
  'patrimony_family_period_snapshots_v1',
  // Ideal budget templates (still localStorage until proper scope migration)
  'patrimony_budget_ideal_personal_v1',
  'patrimony_budget_ideal_family_v1',
  // Misc primary
  'patrimony_loans_archived_v1',
  'patrimony_credit_simulations_v1',
  'patrimony_stocks_wallets_v1',
  'patrimony_crypto_wallets_v1',
];

const SYNC_KEYS_SET = new Set(SYNC_KEYS);
const DEBOUNCE_MS   = 400;

const _migStatusKey = (k) => `migration_status_${k}`;

const _setMigStatus = (key, status, extra = {}) => {
  try {
    // Use the native setItem to avoid recursion through the wrapper
    (window.__nativeSetItem || localStorage.setItem.bind(localStorage))(
      _migStatusKey(key),
      JSON.stringify({ ts: Date.now(), status, ...extra }),
    );
    // eslint-disable-next-line no-console
    console.info(`[userSettingsSync] ${key} → ${status}`, extra);
  } catch {}
};

const _readLSParsed = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw == null ? null : JSON.parse(raw);
  } catch { return null; }
};

const _writeLSRaw = (key, value) => {
  try {
    // bypass the wrapper to avoid triggering a sync push from a hydrate write
    (window.__nativeSetItem || localStorage.setItem.bind(localStorage))(
      key, JSON.stringify(value),
    );
  } catch {}
};

/**
 * Pull every blob from /user-settings in one round-trip and reconcile with
 * localStorage. Cross-device-safe: backend wins when both have a value.
 * For first-time migration of a key that exists locally but not in the
 * backend, push the local value upstream and mark the migration as done.
 */
export const hydrateUserSettings = async () => {
  let backend;
  try {
    const r = await userSettingsApi.getAll();
    backend = r.data || {};
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[userSettingsSync] hydrate pull failed', err);
    return;
  }

  for (const key of SYNC_KEYS) {
    const backendVal = backend[key];

    if (backendVal !== undefined) {
      // Backend has it → authoritative. Write to localStorage (bypass wrapper).
      _writeLSRaw(key, backendVal);
      _setMigStatus(key, 'success', { source: 'backend_pull' });
      continue;
    }

    // Backend doesn't have it. If localStorage does, push it (migration).
    // PUT is idempotent so repeated mounts on the same device are harmless.
    // Multi-user-on-same-browser case: if `localVal` actually belongs to a
    // previously logged-in user (because clearUserData no longer wipes
    // patrimony_* keys), we may push it under the new user. Acceptable
    // transitional risk per Phase 2 design — the alternative (data loss
    // for the legitimate user) is worse.
    const localVal = _readLSParsed(key);
    if (localVal == null) {
      _setMigStatus(key, 'success', { migrated: 0, note: 'no_local_data' });
      continue;
    }
    try {
      await userSettingsApi.set(key, localVal);
      _setMigStatus(key, 'success', { migrated: true });
    } catch (err) {
      _setMigStatus(key, 'error', { error: String(err?.message || err) });
      // Keep localStorage untouched — retry on next mount
    }
  }
};

const _debouncers = new Map();

const _schedulePush = (key, valueStr) => {
  if (_debouncers.has(key)) clearTimeout(_debouncers.get(key));
  _debouncers.set(key, setTimeout(async () => {
    let parsed;
    try { parsed = valueStr == null ? null : JSON.parse(valueStr); }
    catch { parsed = valueStr; }  // tolerate non-JSON strings
    try {
      await userSettingsApi.set(key, parsed);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[userSettingsSync] background sync failed for ${key}`, err);
    }
  }, DEBOUNCE_MS));
};

let _installed = false;

/**
 * Wrap `localStorage.setItem` so SYNC_KEYS writes are mirrored to the
 * backend after a 400 ms debounce. Idempotent — calling this twice does
 * not double-wrap.
 */
export const installUserSettingsAutoSync = () => {
  if (_installed) return;
  _installed = true;
  const native = localStorage.setItem.bind(localStorage);
  // Stash the native impl so hydrate writes can bypass the wrapper.
  window.__nativeSetItem = native;
  localStorage.setItem = function wrappedSetItem(key, value) {
    native(key, value);
    if (typeof key === 'string' && SYNC_KEYS_SET.has(key)) {
      _schedulePush(key, value);
    }
  };
  // Diagnostics
  window.__userSettingsSync = { keys: SYNC_KEYS };
};
